import React, { useState } from 'react';
import {
  Box,
  SpaceBetween,
  Checkbox,
  Select,
  Grid,
  Button
} from '@amzn/awsui-components-react';
import HourlyTab from './HourlyTab';

const Val = ({ children }) => <Box variant="span" fontWeight="bold">{children}</Box>;

const MonthlyTab = ({ isViewMode, onConfigChange, initialConfig = {} }) => {
  const [monthlyWorkingDay, setMonthlyWorkingDay] = useState(false);
  const [monthlyWorkingDayValue, setMonthlyWorkingDayValue] = useState('2');
  const [monthlyWorkingDayMonths, setMonthlyWorkingDayMonths] = useState('3');
  const [monthlyCalendarDay, setMonthlyCalendarDay] = useState(true);
  const [monthlyCalendarDayValue, setMonthlyCalendarDayValue] = useState('1');
  const [monthlyCalendarDayMonths, setMonthlyCalendarDayMonths] = useState('1');
  const [showHourlyConfig, setShowHourlyConfig] = useState(false);
  const [hourlyConfig, setHourlyConfig] = useState({});

  // Initialize from config
  React.useEffect(() => {
    if (Object.keys(initialConfig).length > 0) {
      setMonthlyWorkingDay(initialConfig.workingDay || false);
      setMonthlyWorkingDayValue(initialConfig.workingDayValue || '2');
      setMonthlyWorkingDayMonths(initialConfig.workingDayMonths || '3');
      setMonthlyCalendarDay(initialConfig.calendarDay !== undefined ? initialConfig.calendarDay : true);
      setMonthlyCalendarDayValue(initialConfig.calendarDayValue || '1');
      setMonthlyCalendarDayMonths(initialConfig.calendarDayMonths || '1');
      setShowHourlyConfig(!!initialConfig.hourlyConfig);
      setHourlyConfig(initialConfig.hourlyConfig || {});
    }
  }, [initialConfig]);

  const handleConfigChange = (overrides = {}) => {
    onConfigChange({
      workingDay: monthlyWorkingDay,
      workingDayValue: monthlyWorkingDayValue,
      workingDayMonths: monthlyWorkingDayMonths,
      calendarDay: monthlyCalendarDay,
      calendarDayValue: monthlyCalendarDayValue,
      calendarDayMonths: monthlyCalendarDayMonths,
      hourlyConfig: showHourlyConfig ? hourlyConfig : null,
      ...overrides
    });
  };

  if (isViewMode) {
    const dayType = monthlyWorkingDay ? 'Working day' : 'Calendar day';
    const dayValue = monthlyWorkingDay ? monthlyWorkingDayValue : monthlyCalendarDayValue;
    const months = monthlyWorkingDay ? monthlyWorkingDayMonths : monthlyCalendarDayMonths;
    return (
      <SpaceBetween size="s">
        <Box>
          <Box variant="awsui-key-label">Day type</Box>
          <Val>{dayType}</Val>
        </Box>
        <Box>
          <Box variant="awsui-key-label">Day</Box>
          <Val>{dayValue}</Val>
        </Box>
        <Box>
          <Box variant="awsui-key-label">Every (months)</Box>
          <Val>{months}</Val>
        </Box>
        {showHourlyConfig && hourlyConfig && Object.keys(hourlyConfig).length > 0 && (
          <Box>
            <Box variant="awsui-key-label">Hourly configuration</Box>
            <HourlyTab isViewMode={true} onConfigChange={() => {}} initialConfig={hourlyConfig} />
          </Box>
        )}
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="m">
      <Grid gridDefinition={[{ colspan: 3 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 3 }]}>
        <Box padding={{ top: 'xs' }}>
          <Checkbox
            checked={monthlyWorkingDay}
            onChange={({ detail }) => {
              setMonthlyWorkingDay(detail.checked);
              if (detail.checked) setMonthlyCalendarDay(false);
              handleConfigChange({ workingDay: detail.checked, calendarDay: detail.checked ? false : monthlyCalendarDay });
            }}
            disabled={true}
          >
            Working day
          </Checkbox>
        </Box>
        <Select
          selectedOption={{ label: monthlyWorkingDayValue, value: monthlyWorkingDayValue }}
          onChange={({ detail }) => { setMonthlyWorkingDayValue(detail.selectedOption.value); handleConfigChange({ workingDayValue: detail.selectedOption.value }); }}
          options={[...Array(31)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
          disabled={true}
        />
        <Box padding={{ top: 'xs' }}>of every</Box>
        <Select
          selectedOption={{ label: monthlyWorkingDayMonths, value: monthlyWorkingDayMonths }}
          onChange={({ detail }) => { setMonthlyWorkingDayMonths(detail.selectedOption.value); handleConfigChange({ workingDayMonths: detail.selectedOption.value }); }}
          options={[...Array(12)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
          disabled={true}
        />
        <Box padding={{ top: 'xs' }}>months</Box>
      </Grid>

      <Grid gridDefinition={[{ colspan: 3 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 3 }]}>
        <Box padding={{ top: 'xs' }}>
          <Checkbox
            checked={monthlyCalendarDay}
            onChange={({ detail }) => {
              if (!detail.checked && !monthlyWorkingDay) return;
              setMonthlyCalendarDay(detail.checked);
              if (detail.checked) setMonthlyWorkingDay(false);
              handleConfigChange({ calendarDay: detail.checked, workingDay: detail.checked ? false : monthlyWorkingDay });
            }}
            disabled={isViewMode}
          >
            Calendar day
          </Checkbox>
        </Box>
        <Select
          selectedOption={{ label: monthlyCalendarDayValue, value: monthlyCalendarDayValue }}
          onChange={({ detail }) => { setMonthlyCalendarDayValue(detail.selectedOption.value); handleConfigChange({ calendarDayValue: detail.selectedOption.value }); }}
          options={[...Array(31)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
          disabled={isViewMode}
        />
        <Box padding={{ top: 'xs' }}>of every</Box>
        <Select
          selectedOption={{ label: monthlyCalendarDayMonths, value: monthlyCalendarDayMonths }}
          onChange={({ detail }) => { setMonthlyCalendarDayMonths(detail.selectedOption.value); handleConfigChange({ calendarDayMonths: detail.selectedOption.value }); }}
          options={[...Array(12)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
          disabled={isViewMode}
        />
        <Box padding={{ top: 'xs' }}>months</Box>
      </Grid>

      <Box>
        <Button
          variant="normal"
          onClick={() => { setShowHourlyConfig(!showHourlyConfig); handleConfigChange({ hourlyConfig: !showHourlyConfig ? hourlyConfig : null }); }}
          disabled={isViewMode}
        >
          {showHourlyConfig ? 'Hide' : 'Add'} Hourly Configuration
        </Button>
      </Box>

      {showHourlyConfig && (
        <Box padding={{ left: 'l' }}>
          <Box variant="h3">Hourly Configuration</Box>
          <HourlyTab
            isViewMode={isViewMode}
            onConfigChange={(config) => { setHourlyConfig(config); handleConfigChange({ hourlyConfig: config }); }}
            initialConfig={initialConfig.hourlyConfig || { option: 'interval', interval: '1', selectedHours: [], rangeStart: '9', rangeEnd: '17', specificMinute: '0' }}
          />
        </Box>
      )}
    </SpaceBetween>
  );
};

export default MonthlyTab;
